/**
 * IncidentsSummaryCard - Statistics summary for incidents module
 *
 * FT-90: Implements SLA-based compliance scoring (standalone operational metric)
 * Incidents are NOT included in the SMS Compliance Score - they track operational SLAs.
 * This aligns with industry practice where incidents are managed by time-to-close targets.
 *
 * SLA Formula: (withinSLA + closedInTime) / total × 100
 * Shows: Are incidents being handled within their type-specific deadlines?
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';

const IncidentsSummaryCard = ({ stats }) => {
  const openCount = stats?.open || 0;
  const investigatingCount = stats?.investigating || 0;
  const actionsPendingCount = stats?.actionsPending || 0;
  const closedCount = stats?.closed || 0;
  const totalCount = stats?.total || 0;

  // FT-90: SLA stats from extended incident statistics
  const slaStats = stats?.slaStats || {};
  const withinSLA = slaStats.withinSLA || 0;
  const breached = slaStats.breached || 0;
  const closedInTime = slaStats.closedInTime || 0;
  const closedLate = slaStats.closedLate || 0;

  // FT-90: SLA Compliance (standalone operational metric)
  // Shows effectiveness at handling incidents within SLA deadlines
  const totalTracked = withinSLA + breached + closedInTime + closedLate;
  const slaCompliance = totalTracked === 0 ? 100 :
    Math.round(((withinSLA + closedInTime) / totalTracked) * 100);

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
      {/* Header */}
      <div className="bg-slate-700 px-5 py-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-white" />
          <h3 className="text-base font-semibold text-white">Incident Summary</h3>
        </div>
      </div>

      {/* Content Section */}
      <div className="px-6 py-5">
        {/* FT-90: SLA Compliance Progress Bar (standalone operational metric) */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-600">SLA Compliance</span>
            <span className="text-sm font-semibold text-gray-900">{slaCompliance}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                slaCompliance >= 90 ? 'bg-green-600' :
                slaCompliance >= 70 ? 'bg-amber-500' : 'bg-red-600'
              }`}
              style={{ width: `${slaCompliance}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 mt-1 block">
            {breached > 0 ? `${breached} incident${breached > 1 ? 's' : ''} past SLA` : 'All incidents within SLA'}
          </span>
        </div>

        {/* Status Counts with colored dots */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Open</span>
            </div>
            <span className="text-sm font-semibold text-yellow-600">{openCount}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Investigating</span>
            </div>
            <span className="text-sm font-semibold text-blue-600">{investigatingCount}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Actions Pending</span>
            </div>
            <span className="text-sm font-semibold text-purple-600">{actionsPendingCount}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Closed</span>
            </div>
            <span className="text-sm font-semibold text-green-600">{closedCount}</span>
          </div>
        </div>

        {/* Total */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Total</span>
            <span className="text-sm font-semibold text-gray-900">{totalCount} incidents</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentsSummaryCard;
